// #############################################################################
// #
// #  English translations
// #
// #############################################################################
// This file contains the English translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for English-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to English-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langEn = {
  home: {
    welcome: 'Welcome to the {service} service',
    enter: 'Enter',
  },
  account_type: {
    publisher: "Publisher",
    consumer: "Aggregator",
    author: "Author",
  },
  app: {
    accountNotSubscribedToCurrentService: 'Your account is not subscribed to the current service',
  },
  login: {
    continue: 'Continue',
    login_tab: 'Login',
    logout: 'Logout',
    register_tab: 'Register',
    loginTitle: 'Sign in to your account',
    chooseSignInMethod: 'Choose how to sign in',
    noAccount: "Don't have an account?",
    registerButton: 'Create an account',
    cancel: 'Cancel',
    identifier: 'Identifier',
    identifierDescription: 'You can connect using your email',
    identifierPlaceholder: 'Enter your email',
    usernameOrEmail: 'Username or Email',
    useAnotherAccount: 'Use another account',
    username: 'Username',
    password: 'Password',
    email: 'Email',
    loginButton: 'Login',
    signingIn: 'Signing in...',
    signInWithEmail: 'Send me a one-time link',
    signInWithEmailAgain: 'Request a new one-time link to sign in',
    signInWithEmailDescription: 'Use your account email',
    signingInWithMagicLink: 'Signing in with magic link...',
    signInWithEmailMessageSent: 'A one-time link has been sent to your email, follow the link to sign in.',
    signingInWithEmail: 'Sending one-time link...',
    subscribeButton: 'Subscribe to {type}',
    registerAsButton: 'Register as {type}',
    showPassword: 'Show Password',
    hidePassword: 'Hide Password',
    or: 'or',
    subscriptionSuccess: 'Subscription succeeded',
    alternativeAccountDescription: 'Your account is currently not subscribed to the current service. You can subscribe or switch to the following services.',
    alternativeAccountGoTo: 'Go to {type}',
    sameAccount: 'You are already signed in with the same account.',
    sessionConflict: {
      title: 'Session Conflict',
      message: 'A session is already open for another account.',
      currentSession: 'Current session:',
      magicLinkSession: 'Magic link session:',
      question: 'Do you want to stay connected with the current session or reconnect with the magic link session?',
      stayWithCurrent: 'Stay with current session',
      reconnectWithMagicLink: 'Reconnect with magic link',
    },
    error: {
      invalidCredentials: 'Invalid credentials',
      loginFailed: 'Login failed',
      apiConnection: 'API connection error',
      subscribeFailed: 'Subscribe failed: {error}',
      signInWithEmailFailed: 'Sign in with email failed: {error} (check your email or username).',
      invalidEmailFormat: 'Please enter a valid email address',
    },
    alternativeAccount: {
      contentCreators: 'You are a content creator?',
      contentCreatorsLink: 'Login here',
      authors: 'You are an author?',
      authorsLink: 'Login here',
      aggregators: 'You are an aggregator?',
      aggregatorsLink: 'Login here',
      author: 'Author',
      consumer: 'Aggregator',
      publisher: 'Publisher',
    },
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: '',
        },
      },
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: '',
        },
      },
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: '',
        },
      },
    },
  },
  registration: {
    title: 'Account Registration',
    introduction: {
      title: 'Welcome to Registration',
      subtitle: 'Register for the {service} Service',
      description: 'You are about to register for the {service} service. This registration will allow you to access all features and capabilities available to {service} accounts.',
      startRegistration: 'Start Registration',
      alreadyHaveAccount: 'Already have an account? Login'
    },
    step_introduction: {
      title: 'Introduction',
    },
    step_account_setup: {
      title: 'Account Setup',
      displayName: 'Display Name',
      displayNamePlaceholder: 'Enter your display name',
      username: 'Username',
      usernamePlaceholder: 'Enter your username',
      confirmPassword: 'Confirm Password',
      passwordsDoNotMatch: 'Passwords do not match',
      password: 'Password',
      email: 'Email',
      emailPlaceholder: 'Enter your email address',
      affiliation: 'Affiliation',
      affiliationPlaceholder: 'University, Company, or Institution',
      homePage: 'Home Page / Main Page',
      homePagePlaceholder: 'https://your-website.com',
      submit: 'Submit Profile',
      submitting: 'Submitting...',
      goToAccountVerification: 'Go to account verification',
      goingToVerification: 'Loading...',
      resendActivationFailed: 'Failed to go to verification. Please try again.',
    },
    step_account_verification: {
      title: 'Account Verification',
      tokenLabel: 'Verification Token',
      tokenPlaceholder: 'Enter token',
      checkEmail: 'We have sent a verification email to your address. Please check your inbox and follow the instructions to verify your account.',
      tokenSent: 'Verification token sent to: {email}',
      resendToken: 'Resend Token',
      verifyAccount: 'Verify Account',
      sendingEmail: 'Sending Email...',
      resendEmail: 'Resend Email',
      accountVerified: 'Account verified successfully!',
      redirecting: 'Redirecting to next step...',
      verifying: 'Check Activation',
      sendingToken: 'Sending verification token...',
      invalidToken: 'Invalid verification token. Please try again.',
      tooManyAttempts: 'Too many failed attempts. Please request a new token.',
      noToken: 'No verification token found. Please request a new token.',
      verificationSuccess: 'Email verification successful!',
      attemptsRemaining: '{count} attempts remaining',
      resendSuccess: 'New verification token sent to your email.',
      networkError: 'Network error. Please check your connection and try again.',
      checkingActivation: 'Checking account activation...',
      emailSentTo: 'Email sent to:',
      activationEmailResent: 'Activation email sent successfully! Please check your inbox.',
      resendFailed: 'Failed to send activation email. Please try again.',
      missingIdentifierOrRegistrationToken: 'Missing identifier or registration token',
      accountDoesNotHaveAuthorPrivileges: 'Account does not have author privileges. Please contact support.',
      accountActivationCheckFailed: 'Account activation check failed. Please try again.',
      accountNotYetActivated: 'Account not yet activated. Please check your email and click the activation link.',
      invalidCredentials: 'Invalid credentials. Please check your identifier and password.',
      failedToVerifyAccountActivation: 'Failed to verify account activation. Please try again.',
      failedToResendActivationEmail: 'Failed to resend activation email. Please try again.',
      accountRegistrationRejected: 'Account registration rejected. Please contact support.',
      cancelRegistration: 'Cancel',
    },
    step_registration_complete: {
      title: 'Complete Registration',
      registrationSummary: 'Registration Summary',
      profileCreated: 'Profile created',
      emailsSent: 'Emails sent to publishers',
      continueToDashboard: 'Continue to Dashboard',
      continueToLogin: 'Continue to Login',
      accountCreatedAndVerified: 'Your account has been successfully created and verified!',
      redirecting: 'Redirecting to next step...',
    },
    invitationCode: {
      title: 'Register using an invitation code',
      description: 'If you have received an invitation code, enter it below to proceed with registration.',
      codeLabel: 'Invitation code',
      codePlaceholder: 'Enter your invitation code',
      submit: 'Continue to registration',
      validating: 'Validating...',
      disabledCode: 'This invitation code is disabled.',
      expiredCode: 'This invitation code has expired.',
      limitReachedCode: 'This invitation code has reached the maximum number of uses.',
      unexpectedProductCode: 'This invitation code is for a different product.',
      notFoundCode: 'This invitation code is not found.',
      invalidCode: 'This invitation code is not valid.',
      error: 'Failed to validate the code. Please try again.',
    },
    waitingList: {
      title: 'Register using a waiting list',
      description: 'If you are on the waiting list, enter your email below to proceed with registration.',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email address',
      submit: 'Request code',
      submitting: 'Submitting...',
      success: 'Thank you! You have been added to the waiting list.',
      total: 'Total on waiting list: {total}',
      error: 'Failed to subscribe. Please try again.',
    },
    error: {
      emailRequired: 'Email is required.',
      emailInvalid: 'Please enter a valid email address.',
      submittingFailed: 'Failed to submit. Please try again.',
    },
  },
  common: {
    cancel: 'Cancel',
    save: 'Save',
  },
  navbar: {
    docs: 'Docs',
    account: 'Account',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    pendingRegistrationDesc: 'Please complete your registration to access this service.',
    apps: {
      title: 'Apps',
      publisher: 'Publisher',
      publisherDesc: 'Switch to the publisher service',
      author: 'Author',
      authorDesc: 'Switch to the author service',
      consumer: 'Aggregator',
      consumerDesc: 'Switch to the aggregator service',
      website: 'Website',
      websiteDesc: 'Switch to the website',
      portal: 'Portal',
      portalDesc: 'Switch to the portal',
      currentApplicationDesc: 'Current service',
      unavailableServiceDesc: 'Unavailable service',
    },
  },
  sidebar: {
    expand: 'Expand',
    collapse: 'Collapse',
    info: 'Info',
    infoDesc: 'Information about the service',
    showDescriptions: 'Show descriptions',
    hideDescriptions: 'Hide descriptions',
  },
  pagination: {
    previous: 'Previous',
    next: 'Next',
    first: 'First',
    last: 'Last',
    page: 'Page {current} of {total}',
    itemsPerPage: 'Items per page',
  },
  table: {
    loading: 'Loading...',
    noData: 'No data',
    noResults: 'No results',
    search: 'Search',
  },
  form: {
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading...',
  },
  modal: {
    close: 'Close',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
  },
  alert: {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  },
  toast: {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    close: 'Close',
  },
  tooltip: {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  },
  notification: {
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
  },
  notifications: {
    title: 'Notifications',
    empty: 'No notifications',
    loading: 'Loading notifications...',
    pageInfo: 'Page {current} of {total}',
    error: 'Error loading notifications',
    markAsRead: 'Mark as read',
    markAsUnread: 'Mark as unread',
    open: 'Open',
    markAllAsRead: 'Mark all as read',
    unread: 'Unread',
    all: 'All',
    noContent: 'No content available',
    close: 'Close',
    reload: 'Reload notifications',
    showMore: 'Show more',
    loadingMore: 'Loading more notifications...',
    errorLoadingMore: 'Error loading more notifications',
    noMoreNotifications: 'No more notifications',
    noNotifications: 'No notifications',
    noNotificationsFound: 'No notifications found',
  },
  account: {
    title: 'Account Information',
    profile_section: 'Profile',
    api_keys_section: 'API Keys',
    security_section: 'Security',
    loading: 'Loading account information...',
    profile: 'Profile',
    authorProfile: 'Author Profile',
    publisherProfile: 'Publisher Profile',
    aggregatorProfile: 'Aggregator Profile',
    id: 'ID',
    name: 'Name',
    email: 'Email',
    services: 'Services',
    memberSince: 'Member Since',
    lastLogin: 'Last Login',
    createNewApiKey: 'Create New API Key',
    apiKeysTitle: 'API Keys',
    apiKeys: {
      name: 'Name',
      keyId: 'Key ID',
      status: 'Status',
      lastUsed: 'Last Used',
      created: 'Created',
      expires: 'Expires',
      neverUsed: 'Never Used',
      noKeys: 'No API keys found.',
      advancedControls: 'For advanced controls, visit',
      advancedControlsLink: 'the account management page',
    },
    security: {
      changePassword: 'Change Password',
      changePasswordDescription: 'Update your account password. You will need to enter your current password to confirm the change.',
      currentPassword: 'Current Password',
      currentPasswordPlaceholder: 'Enter your current password',
      newPassword: 'New Password',
      newPasswordPlaceholder: 'Enter your new password',
      confirmPassword: 'Confirm New Password',
      confirmPasswordPlaceholder: 'Re-enter your new password',
      changePasswordButton: 'Change Password',
      changingPassword: 'Changing...',
      passwordChangedSuccess: 'Your password has been changed successfully.',
      passwordChangeFailed: 'Failed to change password. Please check your current password and try again.',
      resetPassword: 'Reset Password',
      resetPasswordDescription: 'Forgot your password? We can send a reset link to your registered email address. Click the link in the email to set a new password.',
      sendResetEmailButton: 'Send Password Reset Email',
      sendingResetEmail: 'Sending...',
      resetEmailSent: 'A password reset email has been sent to your registered address. Please check your inbox and follow the link to set a new password.',
      resetPasswordFailed: 'Failed to send password reset email. Please try again.',
      changePrimaryEmail: 'Change Primary Email',
      changePrimaryEmailDescription: 'Update your primary email address. A verification email will be sent to the new address. The new email will only be activated after you verify it.',
      newEmail: 'New Email',
      newEmailPlaceholder: 'Enter your new email address',
      confirmEmail: 'Confirm New Email',
      confirmEmailPlaceholder: 'Re-enter your new email address',
      emailsDoNotMatch: 'Email addresses do not match',
      changeEmailButton: 'Send Verification Email',
      sendingVerification: 'Sending...',
      emailVerificationSent: 'A verification email has been sent to your new address. Please check your inbox and follow the instructions to activate the new email.',
      emailChangeFailed: 'Failed to request email change. Please try again.',
    },
  },
  profileSetup: {
    displayName: 'Display Name',
    displayNamePlaceholder: 'Enter your display name',
    displayNameHelp: 'Your display name will be used to identify you in the system (this is not your username).',
    title: 'Profile Setup',
    description: 'Please provide your professional information to complete your profile.',
    affiliation: 'Affiliation',
    affiliationPlaceholder: 'e.g., University of Cambridge, MIT, Google Research',
    areaOfInterest: 'Area of Interest',
    areaOfInterestPlaceholder: 'e.g., Machine Learning, Computer Vision, NLP',
    homePage: 'Homepage',
    homePagePlaceholder: 'https://your-website.com',
    secondaryEmails: 'Secondary Emails',
    addSecondaryEmail: 'Add Secondary Email',
    secondaryEmailPlaceholder: 'your email address',
    secondaryEmailsHelp: 'Your secondary emails',
    save: 'Save Profile',
    saving: 'Saving...',
    nextPage: 'Next Page',
    update: 'Update Profile',
    updating: 'Updating...',
    avatarUrl: 'Avatar URL',
    avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
    changeAvatar: 'Change Avatar',
    noAvatar: 'No avatar',
    success: {
      saved: 'Profile saved successfully!',
    },
    errors: {
      affiliationRequired: 'Affiliation is required.',
      areaOfInterestRequired: 'Area of interest is required.',
      invalidHomePage: 'Please enter a valid URL for your homepage.',
      tooManySecondaryEmails: 'You can add a maximum of 5 secondary emails.',
      invalidSecondaryEmail: 'Please enter a valid email address.',
      saveFailed: 'Failed to save profile. Please try again.',
      invalidAvatarUrl: 'Please enter a valid avatar URL.',
    }
  },
  profile: {
    key_DISPLAY_NAME: "Display Name",
    key_NAME: "Name",
    key_EMAIL: "Email",
    key_AFFILIATION: "Affiliation",
    key_AREA_OF_INTEREST: "Area of Interest",
    key_HOME_PAGE: "Homepage",
    key_EMAILS: "Emails",
    key_AVATAR_URL: "Avatar URL",
    key_ORCID: "ORCID",
    key_LINKEDIN: "LinkedIn",
    key_GOOGLE_SCHOLAR: "Google Scholar",
  },  
  profileSetupView: {
    title: 'Profile Setup',
    description: 'Please setup your profile to access this service.',
    update: 'Update Profile',
    editProfile: 'Edit Profile',
  },
  bankInfoForm: {
    title: 'Add/Update Bank Information',
    accountHolder: 'Account Holder Name:',
    iban: 'IBAN:',
    bic: 'BIC/SWIFT Code:',
    save: 'Save Bank Information',
    saved: 'Bank information saved locally (for this demo).',
  },
  settings: {
    title: 'Settings',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    layout: 'Layout',
    helpEnabled: 'Display help messages',
    sidebar: 'Sidebar',
    navbar: 'Navbar',
    save: 'Save',
    saved: 'Settings saved!',
    language: 'Language',
    experimentations: 'Experimentations',
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
  apiKeyModal: {
    title: 'Create New API Key',
    namePlaceholder: 'Name for the API key',
    nameHelp: 'Enter a name for the API key, this will help you identify the key later.',
    createButton: 'Create API Key',
    yourKeyIs: 'Your new API key is:',
    copyWarning: 'Copy and save this key. You won\'t be able to see it again!',
    copy: 'Copy',
    copied: 'Copied!',
    close: 'Close',
    error: 'Failed to create API key.',
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES',
  },
  theme: {
    switchToLight: 'Switch to light mode',
    switchToDark: 'Switch to dark mode',
  },

};